import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import useSEO from 'hooks/useSEO'
import LPNavigation from '../landing/sections/LPNavigation'
import CTABanner from '../landing/sections/CTABanner'
import LPFooter from '../landing/sections/LPFooter'

// ── Mock trade data (mirrors real app data structure) ──────────────────────
const today = new Date()
const d = (offset) => {
  const dt = new Date(today)
  dt.setDate(dt.getDate() + offset)
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const MOCK_TRADES = [
  { ticker: 'AAPL', strategy: 'Covered Call', strike: 195, premium: 2.85, contracts: 2, total: 570, expiry: d(5),  status: 'Open',    ret: 1.50 },
  { ticker: 'JEPI', strategy: 'Cash Secured Put', strike: 54, premium: 0.80, contracts: 5, total: 400, expiry: d(11), status: 'Open',    ret: 1.48 },
  { ticker: 'MSFT', strategy: 'Covered Call', strike: 420, premium: 4.20, contracts: 1, total: 420, expiry: d(18), status: 'Open',    ret: 1.01 },
  { ticker: 'MAIN', strategy: 'Cash Secured Put', strike: 47, premium: 0.60, contracts: 3, total: 180, expiry: d(22), status: 'Open',    ret: 1.28 },
  { ticker: 'KO',   strategy: 'Covered Call', strike: 65,  premium: 0.75, contracts: 3, total: 225, expiry: d(-8),  status: 'Expired', ret: 1.17 },
  { ticker: 'O',    strategy: 'Covered Call', strike: 57,  premium: 0.65, contracts: 5, total: 325, expiry: d(-22), status: 'Closed',  ret: 1.16 },
]

const STATUS_STYLES = {
  Open:    'bg-[#DCFCE7] text-[#166534]',
  Expired: 'bg-[#F1F5F9] text-[#475569]',
  Closed:  'bg-[#FEF9C3] text-[#854D0E]',
  Assigned:'bg-[#FEE2E2] text-[#991B1B]',
  Rolled:  'bg-[#EDE9FE] text-[#6D28D9]',
}

const CC_FAQS = [
  {
    q: 'What is a covered call?',
    a: 'A covered call is when you own at least 100 shares of a stock and sell someone the right to buy them at a set price (the strike) by a set date. You collect a cash premium upfront. If the stock stays below the strike, you keep the shares and the premium. If it goes above, the shares get called away — but you still keep the premium.',
  },
  {
    q: 'What is the wheel strategy?',
    a: 'The wheel is a repeating income cycle: (1) Sell a cash-secured put to collect premium. (2) If assigned, you now own shares cheaply. (3) Sell covered calls against those shares for more premium. Repeat. Dividend investors love it because it layers options income on top of dividend income.',
  },
  {
    q: 'What is a cash-secured put (CSP)?',
    a: 'A cash-secured put is when you sell the right for someone to sell you 100 shares at a set price. You collect premium upfront and keep the cash as collateral. If the stock stays above the strike, you keep the premium. If it falls below, you buy the shares at the strike — which is usually a stock you wanted to own anyway.',
  },
  {
    q: 'Can I track covered calls alongside my dividends?',
    a: 'Yes — Safe Dividend Tracker is built specifically for this. Your covered call and CSP premiums appear in the same income dashboard as your dividend payments, giving you a complete picture of all passive income from your portfolio.',
  },
  {
    q: 'Does it support Webull CSV import?',
    a: 'Yes. You can import trades directly from a Webull CSV export. The tracker normalizes the data, calculates return on collateral for each trade, and adds them to your income history automatically.',
  },
  {
    q: 'Is this free?',
    a: 'The options tracker is available on the Pro plan. Start on the free plan and upgrade to Pro when you\'re ready — no credit card required to get started.',
  },
]

export default function CoveredCallTrackerPage() {
  useSEO({
    title: 'Covered Call Tracker — Wheel Strategy & Options Income',
    description: 'Free covered call and cash-secured put tracker. Log trades, track the wheel strategy, and see your total options income alongside dividends — no spreadsheet needed.',
    canonical: '/covered-call-tracker',
  })

  // Inject WebApplication JSON-LD
  useEffect(() => {
    const id = 'cc-tracker-jsonld'
    if (document.getElementById(id)) return
    const s = document.createElement('script')
    s.id = id
    s.type = 'application/ld+json'
    s.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'Covered Call & Wheel Strategy Tracker',
      url: 'https://safedividendtracker.com/covered-call-tracker',
      description: 'Track covered calls, cash-secured puts, and the wheel strategy alongside dividend income in one free portfolio tracker.',
      applicationCategory: 'FinanceApplication',
      operatingSystem: 'Web, iOS, Android',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    })
    document.head.appendChild(s)
    return () => document.getElementById(id)?.remove()
  }, [])

  const [openFaq, setOpenFaq] = useState(null)

  const openTrades  = MOCK_TRADES.filter(t => t.status === 'Open')
  const closedTrades = MOCK_TRADES.filter(t => t.status !== 'Open')
  const totalOpen   = openTrades.reduce((s, t) => s + t.total, 0)
  const totalClosed = closedTrades.reduce((s, t) => s + t.total, 0)

  return (
    <div className="min-h-screen bg-white">
      <LPNavigation />

      {/* Hero */}
      <section className="pt-28 pb-14 px-4 bg-gradient-to-b from-[#F0FDF4] to-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-medium tracking-widest uppercase text-[#1B5E20] mb-4">Options Income Tracker</p>
          <h1 className="font-display text-5xl lg:text-6xl text-[#0F172A] leading-tight">
            Covered Call &amp; Wheel<br className="hidden sm:block" /> Strategy Tracker
          </h1>
          <p className="mt-5 text-lg text-[#475569] max-w-2xl mx-auto">
            Log covered calls and cash-secured puts, track the wheel strategy cycle, and see your total options income alongside dividends — all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <a
              href="/register"
              className="inline-block bg-[#1B5E20] text-white font-medium text-base px-8 py-4 rounded-full hover:bg-[#145218] hover:-translate-y-px transition-all duration-300 shadow-sm"
            >
              Start Tracking Free
            </a>
            <Link
              to="/drip-calculator"
              className="inline-block bg-white text-[#1B5E20] font-medium text-base px-8 py-4 rounded-full border border-[#1B5E20] hover:bg-[#F0FDF4] transition-all duration-300"
            >
              Try DRIP Calculator
            </Link>
          </div>
          <p className="mt-4 text-xs text-[#94A3B8] tracking-wide uppercase">Free plan forever · No credit card required</p>
        </div>
      </section>

      {/* Mock Trade Table */}
      <section className="px-4 pb-16">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden">
            {/* Table header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0]">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#22C55E]" />
                <span className="text-sm font-semibold text-[#0F172A]">Options Tracker</span>
                <span className="text-xs text-[#94A3B8]">safedividendtracker.com</span>
              </div>
              <div className="flex gap-4 text-xs text-[#64748B]">
                <span>Open premium: <strong className="text-[#1B5E20]">${totalOpen.toLocaleString()}</strong></span>
                <span>Realized: <strong className="text-[#0F172A]">${totalClosed.toLocaleString()}</strong></span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    {['Ticker', 'Strategy', 'Strike', 'Premium', 'Contracts', 'Total', 'Expiry', 'Return %', 'Status'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[#64748B] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MOCK_TRADES.map((t, i) => (
                    <tr key={i} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC] transition-colors">
                      <td className="px-4 py-3 font-semibold text-[#0F172A]">{t.ticker}</td>
                      <td className="px-4 py-3 text-[#475569] whitespace-nowrap">{t.strategy}</td>
                      <td className="px-4 py-3 text-[#0F172A]">${t.strike}</td>
                      <td className="px-4 py-3 text-[#0F172A]">${t.premium}</td>
                      <td className="px-4 py-3 text-[#475569]">{t.contracts}</td>
                      <td className="px-4 py-3 font-semibold text-[#0F172A]">${t.total}</td>
                      <td className="px-4 py-3 text-[#475569] whitespace-nowrap">{t.expiry}</td>
                      <td className="px-4 py-3 text-[#1B5E20] font-medium">{t.ret.toFixed(2)}%</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[t.status]}`}>
                          {t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 bg-[#F8FAFC] border-t border-[#E2E8F0]">
              <p className="text-xs text-[#94A3B8]">Sample data for illustration · Your real trades sync from Webull CSV or manual entry</p>
            </div>
          </div>
        </div>
      </section>

      {/* How the Wheel Works */}
      <section className="bg-[#F8FAFC] px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-medium tracking-widest uppercase text-[#1B5E20] mb-4 text-center">The Strategy</p>
          <h2 className="font-display text-4xl text-[#0F172A] text-center mb-12">How the wheel strategy works</h2>
          <div className="grid md:grid-cols-3 gap-6 relative">
            {/* Connector line on desktop */}
            <div className="hidden md:block absolute top-8 left-1/3 right-1/3 h-0.5 bg-[#DCFCE7]" />
            {[
              {
                step: '01',
                title: 'Sell a Cash-Secured Put',
                body: 'Choose a stock you want to own at a lower price. Sell a put at your target strike and collect premium immediately.',
                tag: 'Collect premium',
              },
              {
                step: '02',
                title: 'Get Assigned (or Repeat)',
                body: 'If the stock stays above your strike, the put expires worthless — you keep the premium and sell another put. If assigned, you buy shares at your strike.',
                tag: 'Own shares cheaply',
              },
              {
                step: '03',
                title: 'Sell Covered Calls',
                body: 'Now sell covered calls against your shares at a strike above your cost basis. Collect more premium while your dividends keep paying.',
                tag: 'Stack income',
              },
            ].map(({ step, title, body, tag }) => (
              <div key={step} className="bg-white border border-[#E2E8F0] rounded-xl p-6 relative">
                <div className="w-10 h-10 rounded-full bg-[#1B5E20] flex items-center justify-center mb-4">
                  <span className="text-xs font-bold text-white">{step}</span>
                </div>
                <span className="text-xs font-medium text-[#1B5E20] bg-[#DCFCE7] px-2 py-0.5 rounded-full">{tag}</span>
                <h3 className="font-semibold text-[#0F172A] mt-3 mb-2">{title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[#94A3B8] mt-8">Then repeat the cycle — compounding income from both dividends and options premiums.</p>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-medium tracking-widest uppercase text-[#1B5E20] mb-4 text-center">What SDT tracks</p>
          <h2 className="font-display text-4xl text-[#0F172A] text-center mb-12">Everything in one tracker</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: '📋', title: 'Covered Calls & CSPs', body: 'Log both strategies with strike, premium, contracts, collateral, expiry, and open date.' },
              { icon: '🎡', title: 'Wheel Cycle View', body: 'See which phase each ticker is in — selling put, assigned, or selling call — and total income per cycle.' },
              { icon: '📊', title: 'Return on Collateral', body: 'Automatically calculates % return on each trade. Net-of-BTC for closed positions with buyback prices.' },
              { icon: '💰', title: 'Combined Income Dashboard', body: 'Options premium and dividend income in one chart. See your true total passive income.' },
              { icon: '📅', title: 'Expiry Management', body: 'Trades sorted by nearest expiry first. See what\'s expiring this week, this month, or already expired.' },
              { icon: '📁', title: 'Webull CSV Import', body: 'Import your full trade history from Webull in one click. No manual entry needed.' },
              { icon: '🔄', title: 'Status Tracking', body: 'Open, Closed, Expired, Assigned, Rolled — full lifecycle tracking with notes per trade.' },
              { icon: '📂', title: 'Multi-Portfolio', body: 'Separate options trades by account — Options Wheel, Retirement IRA, Taxable — across portfolios.' },
            ].map(({ icon, title, body }) => (
              <div key={title} className="flex gap-4 p-4 border border-[#E2E8F0] rounded-xl">
                <span className="text-2xl leading-none mt-0.5">{icon}</span>
                <div>
                  <h3 className="font-semibold text-[#0F172A] mb-1">{title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* vs Spreadsheet */}
      <section className="bg-[#F8FAFC] px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-medium tracking-widest uppercase text-[#1B5E20] mb-4 text-center">Why not a spreadsheet?</p>
          <h2 className="font-display text-4xl text-[#0F172A] text-center mb-10">Your Google Sheet can't do this</h2>
          <div className="overflow-hidden border border-[#E2E8F0] rounded-2xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0F172A]">
                  <th className="px-5 py-4 text-left text-xs font-medium text-[#94A3B8] w-1/2">Feature</th>
                  <th className="px-5 py-4 text-center text-xs font-medium text-[#94A3B8] w-1/4">Spreadsheet</th>
                  <th className="px-5 py-4 text-center text-xs font-medium text-[#22C55E] w-1/4">Safe Dividend Tracker</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Covered call + dividend income combined', false, true],
                  ['Automatic return on collateral calculation', false, true],
                  ['Wheel cycle tracker per ticker', false, true],
                  ['Webull CSV import', false, true],
                  ['Expiry alerts & reminders', false, true],
                  ['Mobile app (iOS + Android)', false, true],
                  ['Manual trade entry', true, true],
                  ['Custom notes per trade', true, true],
                ].map(([feat, sheet, sdt], i) => (
                  <tr key={i} className={`border-b border-[#F1F5F9] ${i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'}`}>
                    <td className="px-5 py-3.5 text-[#0F172A]">{feat}</td>
                    <td className="px-5 py-3.5 text-center">{sheet ? <span className="text-[#22C55E] font-bold">✓</span> : <span className="text-[#CBD5E1]">—</span>}</td>
                    <td className="px-5 py-3.5 text-center"><span className="text-[#1B5E20] font-bold text-base">✓</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white px-4 py-16">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs font-medium tracking-widest uppercase text-[#1B5E20] mb-4 text-center">FAQs</p>
          <h2 className="font-display text-4xl text-[#0F172A] text-center mb-10">Covered call questions</h2>
          {CC_FAQS.map((faq, i) => (
            <div key={i} className="border-b border-[#E2E8F0]">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between py-5 text-left group"
              >
                <span className="text-sm font-medium text-[#0F172A] pr-8 group-hover:text-[#1B5E20] transition-colors">
                  {faq.q}
                </span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
                  className={`shrink-0 text-[#1B5E20] transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}>
                  <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div
                className="overflow-hidden transition-all duration-300 ease-out"
                style={{ maxHeight: openFaq === i ? '250px' : '0', opacity: openFaq === i ? 1 : 0 }}
              >
                <p className="text-sm text-[#475569] leading-relaxed pb-5">{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTABanner
        headline="Start tracking your options income"
        subtext="Covered calls, cash-secured puts, and dividends — all in one free dashboard."
        ctaText="Start Free — No Credit Card"
        microcopy="Free plan forever · No credit card required"
      />
      <LPFooter />
    </div>
  )
}
